/**
 * Hyperliquid key execution readiness — maps Engine HyperliquidKeys.IsActive (and aliases)
 * plus live extra-agent expiry into terminal fail-closed UX.
 */

export type HyperliquidKeySessionFields = {
  requiresHyperliquidOnboarding?: boolean;
  hasHyperliquidKey?: boolean;
  hyperliquidAccountMismatch?: boolean;
  hyperliquidKeyActive?: boolean;
  /** Engine alias for HyperliquidKeys.IsActive */
  hyperliquidKeyIsActive?: boolean;
  isHyperliquidKeyActive?: boolean;
  /** Live extra-agent check: stored API agent expired or revoked. */
  requiresApiKeyRefresh?: boolean;
  apiKeyStatus?: string;
  apiKeyRefreshMessage?: string;
};

export type HyperliquidKeyIssue = 'missing' | 'expired' | 'mismatch' | null;

export const HL_KEY_INACTIVE_BANNER =
  'Your Hyperliquid API key expired. Reconnect to resume Autopilot and live trading.';

export const HL_KEY_INACTIVE_HEADLINE =
  'Hyperliquid API key expired. Reconnect to resume Autopilot.';

export const HL_KEY_MISSING_BANNER =
  'Hyperliquid API key is missing. Complete setup so Oroboros can trade.';

export const HL_KEY_MISSING_HEADLINE = 'Hyperliquid API key is missing. Complete setup to trade.';

export type HyperliquidKeyNotice = {
  kicker: string;
  banner: string;
  headline: string;
  homeTitle: string;
  cta: string;
  action: 'complete_setup' | 'refresh_api_key' | 'switch_account';
};

export function noticeForHyperliquidKeyIssue(issue: HyperliquidKeyIssue): HyperliquidKeyNotice | null {
  if (issue === 'mismatch') {
    return {
      kicker: 'Action required',
      banner:
        'This wallet is linked to a different Jester account. Sign in with the account you trade with (usually Telegram).',
      headline: 'This wallet belongs to a different account.',
      homeTitle: 'Sign in with the account this wallet trades with',
      cta: 'Switch account',
      action: 'switch_account',
    };
  }
  if (issue === 'missing') {
    return {
      kicker: 'Action required',
      banner: HL_KEY_MISSING_BANNER,
      headline: HL_KEY_MISSING_HEADLINE,
      homeTitle: 'Add a Hyperliquid API key so Oroboros can trade',
      cta: 'Complete setup',
      action: 'complete_setup',
    };
  }
  if (issue === 'expired') {
    return {
      kicker: 'Action required',
      banner: HL_KEY_INACTIVE_BANNER,
      headline: HL_KEY_INACTIVE_HEADLINE,
      homeTitle: 'Your Hyperliquid API key expired. Reconnect to resume trading',
      cta: 'Reconnect',
      action: 'refresh_api_key',
    };
  }
  return null;
}

/** Parse session refresh / readiness payloads for HyperliquidKeys.IsActive. */
export function readHyperliquidKeyActiveFromPayload(
  data: Record<string, unknown> | null | undefined,
): boolean | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const candidates = [
    data.hyperliquidKeyActive,
    data.hyperliquidKeyIsActive,
    data.isHyperliquidKeyActive,
  ];
  for (const value of candidates) {
    if (value === true) return true;
    if (value === false) return false;
    if (value === 1 || value === '1') return true;
    if (value === 0 || value === '0') return false;
  }
  return undefined;
}

export function inferHyperliquidKeyInactiveFromBlockers(blockers: string[]): boolean {
  const text = blockers.join(' ').toLowerCase();
  if (!text.trim()) return false;
  return (
    /\b(key|credentials?)\b.*\b(expired|inactive|revoked|invalid|deactivated)\b/.test(text) ||
    /\b(reconnect|re-?onboard)\b.*\b(hyperliquid|hl|wallet|key|trading)\b/.test(text) ||
    /\bhyperliquid\b.*\b(key|onboard|reconnect|expired|inactive)\b/.test(text)
  );
}

/** True when Engine reports an active HL trading key. Unknown → true (legacy Engine). */
export function resolveHyperliquidKeyActive(
  fields: HyperliquidKeySessionFields,
  opts?: { readinessBlockers?: string[] },
): boolean {
  if (fields.requiresApiKeyRefresh) return false;
  const status = String(fields.apiKeyStatus || '').trim().toLowerCase();
  if (status === 'expired' || status === 'revoked' || status === 'none') return false;
  const explicit = readHyperliquidKeyActiveFromPayload(fields as Record<string, unknown>);
  if (explicit === false) return false;
  if (explicit === true) return true;
  if (opts?.readinessBlockers?.length && inferHyperliquidKeyInactiveFromBlockers(opts.readinessBlockers)) {
    return false;
  }
  return true;
}

export function isHyperliquidExecutionReady(
  fields: HyperliquidKeySessionFields,
  opts?: { readinessBlockers?: string[] },
): boolean {
  if (fields.requiresHyperliquidOnboarding) return false;
  if (fields.hyperliquidAccountMismatch) return false;
  if (!fields.hasHyperliquidKey) return false;
  return resolveHyperliquidKeyActive(fields, opts);
}

export function resolveHyperliquidKeyIssue(
  fields: HyperliquidKeySessionFields,
  opts?: { readinessBlockers?: string[] },
): HyperliquidKeyIssue {
  if (fields.hyperliquidAccountMismatch) return 'mismatch';
  const active = resolveHyperliquidKeyActive(fields, opts);
  if (fields.requiresApiKeyRefresh || (fields.hasHyperliquidKey && !active)) {
    return 'expired';
  }
  if (!fields.hasHyperliquidKey) return 'missing';
  return null;
}

export function shouldShowHyperliquidKeyInactiveBanner(
  fields: HyperliquidKeySessionFields,
  opts?: { readinessBlockers?: string[] },
): boolean {
  return resolveHyperliquidKeyIssue(fields, opts) === 'expired';
}

export function shouldShowHyperliquidKeyMissingBanner(
  fields: HyperliquidKeySessionFields,
  opts?: { readinessBlockers?: string[] },
): boolean {
  return resolveHyperliquidKeyIssue(fields, opts) === 'missing';
}
